import { matchPath } from 'react-router-dom';
import type { FeatureFlags } from '@tokenforge/shared';
import type { AppRole } from '../auth/roles';

export type NavigationPlacement = 'primary' | 'secondary' | 'more' | 'utility';

export type NavigationGroupId = 'organization' | 'access' | 'system';

export interface NavigationItem {
  id: string;
  label: string;
  to: string;
  placements: Partial<Record<AppRole, NavigationPlacement>>;
  parentId?: string;
  flag?: keyof FeatureFlags;
  groupId?: NavigationGroupId;
  routePatterns?: readonly string[];
  external?: boolean;
}

export type MoreManagementBlock =
  | { kind: 'item'; key: string; item: NavigationItem }
  | {
      kind: 'group';
      key: string;
      groupId: NavigationGroupId;
      label: string;
      items: NavigationItem[];
    };

const MORE_MANAGEMENT_GROUP_LABELS: Record<NavigationGroupId, string> = {
  organization: '组织成员',
  access: '接入方式',
  system: '系统',
};

export interface VisibleNavigation {
  primary: NavigationItem[];
  more: NavigationItem[];
  utility: NavigationItem[];
  secondaryByParent: Record<string, NavigationItem[]>;
  searchable: NavigationItem[];
}

export const NAVIGATION_ITEMS: readonly NavigationItem[] = [
  {
    id: 'dashboard',
    label: '概览',
    to: '/dashboard',
    placements: { admin: 'primary', dept_admin: 'primary', auditor: 'primary' },
    routePatterns: ['/dashboard'],
  },
  {
    id: 'status',
    label: '服务状态',
    to: '/official-status',
    placements: { admin: 'utility', dept_admin: 'utility', member: 'utility', auditor: 'primary' },
    routePatterns: ['/official-status'],
  },
  {
    id: 'enterprise-api-keys',
    label: 'API Keys',
    to: '/settings/api-keys',
    placements: { admin: 'primary', dept_admin: 'primary' },
    routePatterns: ['/settings/api-keys'],
  },
  {
    id: 'my-api-keys',
    label: 'API Keys',
    to: '/my/api-keys',
    placements: { member: 'primary' },
    routePatterns: ['/my/api-keys'],
  },
  {
    id: 'token-sources',
    label: 'Token 来源',
    to: '/token-sources',
    placements: { admin: 'primary', dept_admin: 'primary' },
    routePatterns: [
      '/token-sources',
      '/token-sources/new',
      '/token-sources/:id',
      '/token-sources/:id/rate-config',
    ],
  },
  {
    id: 'routing',
    label: '路由',
    to: '/routing',
    placements: { admin: 'primary', dept_admin: 'primary' },
    routePatterns: ['/routing', '/routing/:policyId/edit', '/routing/labels', '/routing/providers'],
  },
  {
    id: 'model-inventory',
    label: '模型目录',
    to: '/model-inventory',
    placements: { admin: 'primary', dept_admin: 'primary' },
    flag: 'multi_user.enabled',
    routePatterns: ['/model-inventory'],
  },
  {
    id: 'my-models',
    label: '可用模型',
    to: '/my/models',
    placements: { member: 'primary' },
    routePatterns: ['/my/models'],
  },
  {
    id: 'usage',
    label: '费用与用量',
    to: '/usage',
    placements: { admin: 'primary', dept_admin: 'primary', auditor: 'primary' },
    routePatterns: ['/usage'],
  },
  {
    id: 'my-usage',
    label: '我的用量',
    to: '/my/usage',
    placements: { member: 'primary' },
    routePatterns: ['/my/usage'],
  },
  {
    id: 'leaderboard',
    label: '用量排行',
    to: '/leaderboard',
    placements: { member: 'primary' },
    routePatterns: ['/leaderboard'],
  },
  {
    id: 'attribution',
    label: '用量归因',
    to: '/usage/attribution',
    parentId: 'usage',
    placements: { admin: 'secondary', dept_admin: 'secondary', auditor: 'secondary' },
    flag: 'attribution.three_dim',
    routePatterns: ['/usage/attribution'],
  },
  {
    id: 'reports',
    label: '费用报表',
    to: '/reports',
    parentId: 'usage',
    placements: { admin: 'secondary', dept_admin: 'secondary' },
    flag: 'multi_user.enabled',
    routePatterns: ['/reports', '/reports/builder'],
  },
  {
    id: 'conversations',
    label: '对话资产',
    to: '/conversations',
    placements: { admin: 'primary', dept_admin: 'primary', auditor: 'primary' },
    flag: 'conversations.retention',
    routePatterns: ['/conversations', '/conversations/:id'],
  },
  {
    id: 'wiki',
    label: 'Wiki 知识库',
    to: '/conversations/wiki',
    parentId: 'conversations',
    placements: { admin: 'secondary', dept_admin: 'secondary', auditor: 'secondary' },
    flag: 'wiki.auto_generate',
    routePatterns: ['/conversations/wiki', '/conversations/wiki/:slug'],
  },
  {
    id: 'conversation-classify',
    label: '对话归类',
    to: '/conversations/classify',
    parentId: 'conversations',
    placements: { admin: 'secondary', dept_admin: 'secondary' },
    flag: 'conversations.classification',
    routePatterns: ['/conversations/classify'],
  },
  {
    id: 'knowledge-graph',
    label: '知识图谱',
    to: '/conversations/graph',
    parentId: 'conversations',
    placements: { admin: 'secondary', dept_admin: 'secondary' },
    flag: 'knowledge_graph.enabled',
    routePatterns: ['/conversations/graph'],
  },
  {
    id: 'rag-savings',
    label: 'RAG 降本',
    to: '/conversations/rag-savings',
    parentId: 'conversations',
    placements: { admin: 'secondary', dept_admin: 'secondary' },
    flag: 'rag.savings_panel',
    routePatterns: ['/conversations/rag-savings'],
  },
  {
    id: 'audit',
    label: '审计证据',
    to: '/security/audit',
    placements: { admin: 'primary', dept_admin: 'more', auditor: 'primary' },
    flag: 'audit.archive',
    routePatterns: ['/security/audit'],
  },
  {
    id: 'dlp',
    label: 'DLP 规则',
    to: '/security/rules',
    parentId: 'audit',
    placements: { admin: 'secondary', dept_admin: 'secondary' },
    flag: 'dlp.hard_block',
    routePatterns: ['/security/rules', '/security/rules/:id/edit', '/settings/dlp-rules'],
  },
  {
    id: 'onboarding',
    label: '配置引导',
    to: '/onboarding',
    placements: { admin: 'more', dept_admin: 'more' },
    routePatterns: ['/onboarding'],
  },
  {
    id: 'members',
    label: '成员',
    to: '/org/members',
    placements: { admin: 'more', dept_admin: 'more' },
    flag: 'multi_user.enabled',
    groupId: 'organization',
    routePatterns: ['/org/members'],
  },
  {
    id: 'departments',
    label: '部门',
    to: '/org/departments',
    placements: { admin: 'more', dept_admin: 'more' },
    flag: 'multi_user.enabled',
    groupId: 'organization',
    routePatterns: ['/org/departments'],
  },
  {
    id: 'self-registration',
    label: '自助注册',
    to: '/org/self-registration',
    placements: { admin: 'more' },
    flag: 'multi_user.enabled',
    groupId: 'access',
    routePatterns: ['/org/self-registration'],
  },
  {
    id: 'directory',
    label: '目录同步',
    to: '/integrations/directory',
    placements: { admin: 'more' },
    flag: 'directory.sync',
    groupId: 'access',
    routePatterns: ['/integrations/directory'],
  },
  {
    id: 'oidc',
    label: 'OIDC 登录',
    to: '/settings/oidc-providers',
    placements: { admin: 'more' },
    flag: 'sso.full',
    groupId: 'access',
    routePatterns: ['/settings/oidc-providers', '/org/sso'],
  },
  {
    id: 'org-settings',
    label: '企业设置',
    to: '/settings/org',
    placements: { admin: 'more' },
    groupId: 'system',
    routePatterns: ['/settings/org'],
  },
  {
    id: 'about',
    label: '系统信息',
    to: '/settings/about',
    placements: { admin: 'more' },
    groupId: 'system',
    routePatterns: ['/settings/about', '/settings/license', '/settings/general'],
  },
  {
    id: 'diagnostic',
    label: '网关诊断',
    to: '/diagnostic',
    placements: { admin: 'utility', dept_admin: 'more', member: 'utility' },
    routePatterns: ['/diagnostic'],
  },
  {
    id: 'chat',
    label: '调用调试',
    to: '/chat',
    placements: { admin: 'utility', dept_admin: 'utility', member: 'utility' },
    routePatterns: ['/chat'],
  },
  {
    id: 'playground',
    label: 'Playground',
    to: '/playground',
    placements: {
      admin: 'utility',
      dept_admin: 'utility',
      member: 'utility',
      auditor: 'utility',
    },
    routePatterns: ['/playground'],
  },
  {
    id: 'docs',
    label: '文档',
    to: 'https://www.tokgoai.com/docs/',
    external: true,
    placements: { admin: 'utility', dept_admin: 'utility', member: 'utility', auditor: 'utility' },
  },
];

function itemEnabled(item: NavigationItem, flags: FeatureFlags): boolean {
  return item.flag === undefined || flags[item.flag];
}

export function buildVisibleNavigation(role: AppRole, flags: FeatureFlags): VisibleNavigation {
  const eligible = NAVIGATION_ITEMS.filter(
    (item) => item.placements[role] !== undefined && itemEnabled(item, flags),
  );
  const eligibleIds = new Set(eligible.map((item) => item.id));
  const visible = eligible.filter((item) => !item.parentId || eligibleIds.has(item.parentId));
  const secondaryByParent = visible
    .filter((item) => item.placements[role] === 'secondary' && item.parentId)
    .reduce<Record<string, NavigationItem[]>>((groups, item) => {
      const parentId = item.parentId!;
      groups[parentId] = [...(groups[parentId] ?? []), item];
      return groups;
    }, {});

  return {
    primary: visible.filter((item) => item.placements[role] === 'primary'),
    more: visible.filter((item) => item.placements[role] === 'more'),
    utility: visible.filter((item) => item.placements[role] === 'utility'),
    secondaryByParent,
    searchable: visible,
  };
}

export function buildMoreManagementBlocks(items: readonly NavigationItem[]): MoreManagementBlock[] {
  const blocks: MoreManagementBlock[] = [];

  for (const item of items) {
    const previous = blocks[blocks.length - 1];
    if (item.groupId && previous?.kind === 'group' && previous.groupId === item.groupId) {
      previous.items.push(item);
      continue;
    }
    if (item.groupId) {
      blocks.push({
        kind: 'group',
        key: `group:${item.groupId}:${blocks.length}`,
        groupId: item.groupId,
        label: MORE_MANAGEMENT_GROUP_LABELS[item.groupId],
        items: [item],
      });
      continue;
    }
    blocks.push({ kind: 'item', key: `item:${item.id}`, item });
  }

  return blocks;
}

function routeScore(item: NavigationItem, pathname: string): number {
  return Math.max(
    -1,
    ...(item.routePatterns ?? []).map((pattern) =>
      matchPath({ path: pattern, end: true }, pathname)
        ? pattern.replace(/:[^/]+/g, '').length
        : -1,
    ),
  );
}

export function findActiveNavigationItem(
  pathname: string,
  items: readonly NavigationItem[],
): NavigationItem | null {
  return (
    items
      .map((item) => ({ item, score: routeScore(item, pathname) }))
      .filter(({ score }) => score >= 0)
      .sort((left, right) => right.score - left.score)[0]?.item ?? null
  );
}

export function resolveManagedRouteAccess(
  pathname: string,
  role: AppRole,
  flags: FeatureFlags,
): 'allow' | 'deny' | 'unmanaged' {
  const managed = findActiveNavigationItem(
    pathname,
    NAVIGATION_ITEMS.filter((item) => !item.external),
  );
  if (!managed) return 'unmanaged';
  const visible = findActiveNavigationItem(
    pathname,
    buildVisibleNavigation(role, flags).searchable,
  );
  return visible?.id === managed.id ? 'allow' : 'deny';
}
